import { Alert, Form } from 'antd';
import { loginToApp } from 'api/authentication/authen.api';
import CheckboxCustom from 'components/Checkbox';
import Link from 'components/Link';
import useCheckErrorCode from 'hook/useCheckErrorCode';
import useRemoveBlankSpace from 'hook/useRemoveBlankSpace';
import useSetTitlePage from 'hook/useSetTitlePage';
import BtnSubmitFullSize from 'page/Authentication/component/Button/BtnSubmitFullSize';
import {
  ValidateEmail,
  ValidatePasswordEmpty,
} from 'page/Authentication/component/Input';
import useErrorCodeSignIn from 'page/Authentication/SignIn/hook/useErrorCodeSignIn';
import useMultiLanguageSignIn from 'page/Authentication/SignIn/hook/useMultiLanguageSignIn';
import {
  FormItemStyled,
  TitleAuthentication,
} from 'page/Authentication/styled';
import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  authenticationToken,
  partnerIdRecoil,
  userAccount,
  userInformation,
} from 'recoil/atom/authentication';
import useCheckRouterValid from 'hook/useCheckRouterValid';

const SignIn = () => {
  const {
    SIGN_IN_TITLE,
    SIGN_IN_REMEMBER_ME,
    SIGN_IN_FORGOT_PASSWORD,
    MAINTENANCE_MESSAGE,
  } = useMultiLanguageSignIn();
  useSetTitlePage('Solitonパートナーサイト│ログイン', true);
  const navigate = useNavigate();
  const [rememberUser, setRememberUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setToken] = useRecoilState(authenticationToken);
  const [, setUserInformation] = useRecoilState(userInformation);
  const [, setPartnerId] = useRecoilState(partnerIdRecoil);
  const [currentUserAccount, setCurrentUserAccount] =
    useRecoilState(userAccount);
  const { ERROR_CODE_SIGN_IN } = useErrorCodeSignIn();
  const { handleClearErrorMessage, handleRenderMessage, errorMessage } =
    useCheckErrorCode({ errorCode: ERROR_CODE_SIGN_IN });
  const { handleTrimInput } = useRemoveBlankSpace();
  const { checkRouterValid } = useCheckRouterValid();
  const { role } = useRecoilValue(userInformation);

  const [form] = Form.useForm();

  const { mutate, isError } = useMutation({
    mutationFn: loginToApp,
    onSuccess: (response) => {
      setLoading(false);
      handleClearErrorMessage();
      let errorCode = (response.ec ??= false);
      const formData = form.getFieldsValue(true);
      const responseData = response.data;
      // save token to localStorage
      if (errorCode === false) {
        setPartnerId({
          partnerId: responseData?.userResponse?.partnerId,
        });
        setToken({
          accessToken: responseData.accessToken,
          passwordExpiredRemainingDay: responseData.passwordExpiredRemainingDay,
        });

        setUserInformation({
          ...responseData?.userResponse,
        });
        // save user & password to localStorage
        if (rememberUser) {
          setCurrentUserAccount({
            email: formData.email,
            password: formData.password,
            remember: true,
          });
        } else {
          setCurrentUserAccount({
            email: '',
            password: '',
            remember: false,
          });
        }
      }
    },
    onError: (response) => {
      setLoading(false);
      handleRenderMessage(response);
    },
  });

  useEffect(() => {
    const roles = {
      ROLE_PARTNER_USER: {
        path: `/partner-page`,
      },
      ROLE_PARTNER_ADMIN: {
        path: `/partner-page`,
      },
      ROLE_SOLITON_USER: {
        path: '/my-page',
      },
      ROLE_SOLITON_ADMIN: {
        path: '/manage-company-list',
      },
    };

    if (checkRouterValid) {
      const targetPath = roles[role].path;
      navigate(targetPath);
    }
  }, [role, checkRouterValid]);

  const onFinish = (values) => {
    const payload = {
      email: values.email,
      password: values.password,
    };
    setLoading(true);
    mutate(payload);
  };

  useEffect(() => {
    if (currentUserAccount.email !== '' && currentUserAccount.password !== '') {
      form.setFieldsValue({
        email: currentUserAccount.email,
        password: currentUserAccount.password,
      });
      setRememberUser(currentUserAccount.remember);
    }
  }, [form, currentUserAccount]);

  // start get maintenance information
  const [yyyy, setyyyy] = useState();
  const [MM, setMM] = useState();
  const [dd, setdd] = useState();
  const [hh, sethh] = useState();
  const [mm, setmm] = useState();
  const [isMaintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('./maintenancetimeconfig.json');
        const jsonData = await response.json();
        setyyyy(jsonData.yyyy);
        setMM(jsonData.MM);
        setdd(jsonData.dd);
        sethh(jsonData.hh);
        setmm(jsonData.mm);
        setMaintenance(jsonData.isMaintenance);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData().then();
  }, []);

  const message = MAINTENANCE_MESSAGE.replace('{yyyy}', yyyy)
    .replace('{MM}', MM)
    .replace('{dd}', dd)
    .replace('{hh}', hh)
    .replace('{mm}', mm);

  const formattedMessage = message.split('\n')?.map((str, index, arr) => (
    <span key={index}>
      {str}
      {index < arr?.length - 1 && <br />}
    </span>
  ));

  return (
    <>
      <div className="form-responsive">
        {isMaintenance && (
          <Alert
            message={formattedMessage}
            type="error"
            style={{ paddingLeft: '30px' }}
          />
        )}

        <TitleAuthentication className="title-form-sign-in-responsive">
          {SIGN_IN_TITLE}
        </TitleAuthentication>

        {/* show error when call API */}
        {(isError || errorMessage) && (
          <Alert
            message={errorMessage}
            type="error"
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* start form sign in */}
        <Form
          form={form}
          name="normal_login"
          className="size"
          onFinish={onFinish}
          layout={'vertical'}
        >
          {/* start email */}
          <ValidateEmail
            name="email"
            onChange={(e) => handleTrimInput(form, 'email', e.target.value)}
          />
          {/* end email */}

          {/* start password */}
          <ValidatePasswordEmpty
            name="password"
            onChange={(e) => handleTrimInput(form, 'password', e.target.value)}
          />
          {/* end password */}

          {/* start checkbox remember account - link navigate to forgot password */}
          <FormItemStyled marginbottom="15px">
            <div className="text-wrapper">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <CheckboxCustom
                  text={SIGN_IN_REMEMBER_ME}
                  value={rememberUser}
                  onChange={(e) => {
                    setRememberUser(e.target.checked);
                  }}
                />
              </Form.Item>
              <div>
                <Link
                  // to={isMaintenance ? SIGN_IN_PATH : '/forgot-password'}
                  to={'/forgot-password'}
                  text={SIGN_IN_FORGOT_PASSWORD}
                />
              </div>
            </div>
          </FormItemStyled>
          {/* end checkbox remember account - link navigate to forgot password */}

          {/* start button sign in */}
          <BtnSubmitFullSize
            button="login"
            formName="sign-in"
            loading={loading}
            isMaintenance={isMaintenance}
          />
          {/* end button sign in */}
        </Form>
      </div>
      {/* <div className="blank-space-sign-in">
        {window.location.host === 'localhost:3000' ? (
          <span>
            Version: {appVersion.current} -- Last update:{' '}
            {new Date(appVersion.timestamp * 1000).toLocaleString('vi')}
          </span>
        ) : null}
      </div> */}
    </>
  );
};

export default SignIn;
